// src/hr/timeoff/PoliciesPage.js -- policies assigned to me (GET /api/hr/policies/mine).
// Open (a new tab: the PDF through the API, or HR's https link) and "I've read this", which records
// the exact version.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiAlertCircle, FiCheck, FiExternalLink, FiFileText } from 'react-icons/fi';
import { hr, errorText } from '../api';
import { Btn, Card, EmptyState, Pill, Skeleton } from '../kit';
import { shortDate, timestampLabel, weekdayShort } from './dates';

function useMyPolicies() {
  const [state, setState] = useState({ status: 'loading', rows: [] });
  const live = useRef(true);
  const load = useCallback(async (quiet = false) => {
    if (!quiet) setState((s) => ({ ...s, status: 'loading' }));
    try {
      const rows = await hr.myPolicies();
      if (live.current) setState({ status: 'ready', rows: Array.isArray(rows) ? rows : [] });
    } catch (err) {
      if (live.current) setState({ status: 'error', rows: [] });
    }
  }, []);
  useEffect(() => {
    live.current = true;
    load();
    return () => { live.current = false; };
  }, [load]);
  return { ...state, reload: load };
}

function PolicyRow({ policy, today, onAck }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const done = !!policy.ackAt;
  const overdue = !done && policy.dueDate && policy.dueDate < today;
  const href = policy.url || hr.policyFileUrl(policy.id);

  let meta = `Version ${policy.version}`;
  if (done) meta += ` · you confirmed ${timestampLabel(policy.ackAt)}`;
  else if (policy.dueDate) meta += ` · ${overdue ? 'was due' : 'read by'} ${weekdayShort(policy.dueDate)}, ${shortDate(policy.dueDate)}`;

  const ack = async () => {
    setBusy(true);
    setError('');
    try {
      await onAck(policy);
    } catch (err) {
      setError(errorText(err, "Couldn't record that. Try again."));
      setBusy(false);
    }
  };

  return (
    <li className="hrp-pol" data-state={done ? 'done' : overdue ? 'overdue' : 'todo'}>
      <span className="hrp-pol__ic" aria-hidden="true">{done ? <FiCheck /> : <FiFileText />}</span>
      <div className="hrp-pol__txt">
        <b>{policy.title}</b>
        <span>{meta}</span>
      </div>
      <span className="hrp-pol__state">
        {done && <Pill tone="ok" dot>Read</Pill>}
        {!done && overdue && <Pill tone="bad" dot>Overdue</Pill>}
        {!done && !overdue && <Pill tone="warn" dot>Needs your acknowledgment</Pill>}
      </span>
      <span className="hrp-pol__acts">
        <Btn variant="ghost" size="sm" href={href} target="_blank" iconAfter={FiExternalLink} aria-label={`Open ${policy.title} in a new tab`}>Open</Btn>
        {!done && <Btn size="sm" icon={FiCheck} loading={busy} onClick={ack}>I've read this</Btn>}
      </span>
      {error && <p className="hrp-pol__err" role="alert"><FiAlertCircle aria-hidden="true" />{error}</p>}
    </li>
  );
}

export function PoliciesPage({ today, onToast, onChanged }) {
  const { status, rows, reload } = useMyPolicies();
  const todo = rows.filter((p) => !p.ackAt);
  const done = rows.filter((p) => p.ackAt);

  const ack = async (policy) => {
    await hr.ackPolicy(policy.id);
    onToast('Thanks — your acknowledgment is recorded');
    await reload(true);
    if (onChanged) onChanged();
  };

  return (
    <div className="hrp-page">
      <div className="hrp-page-head">
        <div>
          <h1 className="hrp-h1">Policies</h1>
          <p className="hrp-sub">
            {status === 'ready' && todo.length > 0
              ? `${todo.length} waiting on you · open each one, then confirm you've read it`
              : 'Company policies assigned to you'}
          </p>
        </div>
      </div>

      {status === 'loading' && (
        <Card><Skeleton label="Loading your policies" lines={5} height={14} /></Card>
      )}
      {status === 'error' && (
        <Card>
          <EmptyState compact icon={FiAlertCircle} title="Couldn't load your policies" action={<Btn variant="ghost" size="sm" onClick={() => reload()}>Try again</Btn>}>
            Something went wrong on our side.
          </EmptyState>
        </Card>
      )}
      {status === 'ready' && rows.length === 0 && (
        <Card>
          <EmptyState icon={FiFileText} title="No policies assigned to you">
            When HR shares a policy with you, it shows up here.
          </EmptyState>
        </Card>
      )}
      {status === 'ready' && rows.length > 0 && (
        <div className="hrp-stack">
          {todo.length > 0 && (
            <Card delay={0} title="To read" className="hrp-polcard">
              <ul className="hrp-pol__list">
                {todo.map((p) => <PolicyRow key={p.id} policy={p} today={today} onAck={ack} />)}
              </ul>
            </Card>
          )}
          {done.length > 0 && (
            <Card delay={1} title="Done" className="hrp-polcard">
              <ul className="hrp-pol__list">
                {done.map((p) => <PolicyRow key={p.id} policy={p} today={today} onAck={ack} />)}
              </ul>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

export default PoliciesPage;
